import { useEffect, useRef, useState } from "react";
import { SpeakerHigh, Pause, Play, Stop, SkipBack, SkipForward, CaretDown, CaretUp } from "@phosphor-icons/react";
import { useLang } from "@/components/LanguageSelector";
import { toast } from "sonner";

/**
 * ListenButton — browser-native TTS "Listen to" widget.
 *
 * • Reads the visible text from a DOM subtree (default: `[data-listen-root]`).
 * • Respects the current NaturePulse language (from `useLang`).
 * • Chunks long articles into ~250-char pieces to avoid the browser's TTS
 *   time-limit bug (Chrome cuts off long utterances around 15s).
 * • Play → Pause → Resume → Stop, plus ±chunk skip, playback speed and
 *   voice picker for a studio-quality feel — all offline, all free.
 *
 * Usage: mark the article container with `data-listen-root` and render
 * <ListenButton /> anywhere on the page.
 */

// Map our internal language codes to BCP47 tags the browser SpeechSynthesis
// engines recognise. Falls back to the code itself if not found.
const LANG_TO_BCP47 = {
  en: "en-US", hi: "hi-IN", bn: "bn-IN", ta: "ta-IN", te: "te-IN",
  mr: "mr-IN", gu: "gu-IN", kn: "kn-IN", ml: "ml-IN", pa: "pa-IN",
  or: "or-IN", as: "as-IN", ur: "ur-PK", sa: "hi-IN", kok: "hi-IN",
  es: "es-ES", fr: "fr-FR", de: "de-DE", pt: "pt-BR", ar: "ar-SA",
  "zh-CN": "zh-CN", ja: "ja-JP", ru: "ru-RU",
};

const CHUNK_CHAR_LIMIT = 240;
const RATE_OPTIONS = [
  { key: 0.75, label: "0.75×" },
  { key: 1.0, label: "1×" },
  { key: 1.25, label: "1.25×" },
  { key: 1.5, label: "1.5×" },
  { key: 1.75, label: "1.75×" },
];

function pickVoice(bcp47) {
  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return null;
  const exact = voices.find((v) => v.lang?.toLowerCase() === bcp47.toLowerCase());
  if (exact) return exact;
  const prefix = bcp47.split("-")[0].toLowerCase();
  return voices.find((v) => v.lang?.toLowerCase().startsWith(prefix)) || voices[0];
}

// Extract readable, ordered text from a container. Uses heading + block
// separators so the speech feels natural. Skips scripts, styles and any
// element opted out via `[data-listen-skip]`.
function extractText(root) {
  if (!root) return [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (el) => {
      if (el.closest("[data-listen-skip]")) return NodeFilter.FILTER_REJECT;
      const tag = el.tagName;
      if (["SCRIPT", "STYLE", "NOSCRIPT", "BUTTON", "SVG", "INPUT", "SELECT"].includes(tag))
        return NodeFilter.FILTER_REJECT;
      if (["H1", "H2", "H3", "H4", "P", "LI", "BLOCKQUOTE"].includes(tag)) {
        return NodeFilter.FILTER_ACCEPT;
      }
      return NodeFilter.FILTER_SKIP;
    },
  });
  const parts = [];
  while (walker.nextNode()) {
    const el = walker.currentNode;
    const txt = (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim();
    if (!txt) continue;
    if (["H1", "H2", "H3", "H4"].includes(el.tagName)) parts.push({ txt, pause: true });
    else parts.push({ txt, pause: false });
  }
  return parts;
}

// Split a long text into speakable chunks (sentence-first, then char-limit).
function chunk(text) {
  if (text.length <= CHUNK_CHAR_LIMIT) return [text];
  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];
  const out = [];
  let buf = "";
  for (const s of sentences) {
    if ((buf + s).length > CHUNK_CHAR_LIMIT && buf) { out.push(buf.trim()); buf = s; }
    else buf += s;
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

export default function ListenButton({
  rootSelector = "[data-listen-root]",
  label = "Listen",
  className = "",
  variant = "chip",
  testid = "listen-play",
}) {
  const lang = useLang();
  const [state, setState] = useState("idle"); // idle | playing | paused
  const [progress, setProgress] = useState(0); // 0..1
  const [rate, setRate] = useState(() => {
    try {
      const raw = localStorage.getItem("np:tts-rate");
      const v = raw ? parseFloat(raw) : 1;
      return v >= 0.5 && v <= 2 ? v : 1;
    } catch { return 1; }
  });
  const [voiceKey, setVoiceKey] = useState(() => {
    try { return localStorage.getItem("np:tts-voice") || ""; } catch { return ""; }
  });
  const [voices, setVoices] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const queueRef = useRef([]);
  const idxRef = useRef(0);
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;

  // Whenever the language changes mid-playback, stop — user can restart in the new lang.
  useEffect(() => {
    if (!supported) return;
    return () => window.speechSynthesis.cancel();
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    if (state !== "idle") stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Populate voice list (async in most browsers).
  useEffect(() => {
    if (!supported) return;
    const refresh = () => setVoices(window.speechSynthesis.getVoices() || []);
    refresh();
    window.speechSynthesis.addEventListener?.("voiceschanged", refresh);
    return () => {
      window.speechSynthesis.removeEventListener?.("voiceschanged", refresh);
    };
  }, [supported]);

  useEffect(() => {
    try { localStorage.setItem("np:tts-rate", String(rate)); } catch { /* noop */ }
  }, [rate]);
  useEffect(() => {
    try { localStorage.setItem("np:tts-voice", voiceKey || ""); } catch { /* noop */ }
  }, [voiceKey]);

  if (!supported) return null;

  const bcp47 = LANG_TO_BCP47[lang] || lang || "en-US";
  const langVoices = voices.filter((v) => v.lang?.toLowerCase().startsWith(bcp47.split("-")[0].toLowerCase()));

  const speakNext = () => {
    const q = queueRef.current;
    const i = idxRef.current;
    if (i >= q.length) { setState("idle"); setProgress(1); return; }
    const utter = new SpeechSynthesisUtterance(q[i]);
    utter.lang = bcp47;
    // Voice selection: user-picked → language match → default.
    let voice = null;
    if (voiceKey) voice = voices.find((v) => `${v.name}::${v.lang}` === voiceKey) || null;
    if (!voice) voice = pickVoice(bcp47);
    if (voice) {
      try { utter.voice = voice; } catch { /* engine rejected — fall back to lang match */ }
    }
    utter.rate = rate;
    utter.pitch = 1.0;
    utter.onend = () => {
      idxRef.current = i + 1;
      setProgress((i + 1) / q.length);
      speakNext();
    };
    utter.onerror = (e) => {
      // "canceled" is not a real error — happens on stop()
      if (e?.error && e.error !== "canceled" && e.error !== "interrupted") {
        toast.error("Speech engine hiccuped — try again.");
      }
      setState("idle");
    };
    window.speechSynthesis.speak(utter);
  };

  const play = () => {
    const root = document.querySelector(rootSelector);
    if (!root) { toast.info("Nothing to read on this page."); return; }
    const parts = extractText(root);
    const flat = parts.flatMap((p) => chunk(p.txt + (p.pause ? "." : "")));
    if (flat.length === 0) { toast.info("Nothing to read here."); return; }
    // Trigger voice list load on some browsers
    window.speechSynthesis.cancel();
    setTimeout(() => {
      queueRef.current = flat;
      idxRef.current = 0;
      setProgress(0);
      setState("playing");
      speakNext();
    }, 60);
  };

  const pause = () => { window.speechSynthesis.pause(); setState("paused"); };
  const resume = () => { window.speechSynthesis.resume(); setState("playing"); };
  const stop = () => {
    window.speechSynthesis.cancel();
    queueRef.current = []; idxRef.current = 0;
    setProgress(0); setState("idle");
  };

  const skip = (delta) => {
    // Move the pointer, cancel current utterance so onerror doesn't advance too.
    const q = queueRef.current;
    if (!q.length) return;
    const next = Math.min(q.length - 1, Math.max(0, idxRef.current + delta));
    idxRef.current = next;
    setProgress(next / q.length);
    // Cancel triggers `interrupted`; we manually restart.
    window.speechSynthesis.cancel();
    setState("playing");
    setTimeout(() => speakNext(), 60);
  };

  const applyRate = (v) => {
    setRate(v);
    if (state === "playing" || state === "paused") {
      // Restart the current chunk with new rate.
      window.speechSynthesis.cancel();
      setState("playing");
      setTimeout(() => speakNext(), 60);
    }
  };

  const applyVoice = (key) => {
    setVoiceKey(key);
    if (state === "playing" || state === "paused") {
      window.speechSynthesis.cancel();
      setState("playing");
      setTimeout(() => speakNext(), 60);
    }
  };

  const chipBase = "inline-flex items-center gap-2 rounded-full font-mono-tech text-[10px] tracking-[0.28em] uppercase transition-colors";
  const chipStyle = variant === "dark"
    ? "border border-[#F5EBDC]/30 text-[#F5EBDC] hover:bg-[#F5EBDC] hover:text-[#0A1620] px-3.5 py-1.5"
    : "border border-[color:var(--np-brand,#B85F2A)] text-[color:var(--np-brand,#B85F2A)] hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white px-3.5 py-1.5";

  if (state === "idle") {
    return (
      <div className={`inline-flex items-center gap-1.5 ${className}`}>
        <button
          onClick={play}
          data-testid={testid}
          aria-label="Listen to page"
          className={`${chipBase} ${chipStyle}`}
        >
          <SpeakerHigh size={12} weight="fill" /> {label}
        </button>
        <SettingsButton
          rate={rate}
          voices={langVoices}
          voiceKey={voiceKey}
          open={showSettings}
          onToggle={() => setShowSettings((v) => !v)}
          onRate={applyRate}
          onVoice={applyVoice}
          variant={variant}
        />
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-1.5 flex-wrap ${className}`} data-testid="listen-controls">
      <button
        onClick={() => skip(-1)}
        data-testid="listen-skip-back"
        aria-label="Skip back"
        className={`${chipBase} ${chipStyle}`}
      >
        <SkipBack size={11} weight="fill" />
      </button>
      {state === "playing" ? (
        <button
          onClick={pause}
          data-testid="listen-pause"
          aria-label="Pause"
          className={`${chipBase} ${chipStyle}`}
        >
          <Pause size={12} weight="fill" /> Pause · {Math.round(progress * 100)}%
        </button>
      ) : (
        <button
          onClick={resume}
          data-testid="listen-resume"
          aria-label="Resume"
          className={`${chipBase} ${chipStyle}`}
        >
          <Play size={12} weight="fill" /> Resume · {Math.round(progress * 100)}%
        </button>
      )}
      <button
        onClick={() => skip(1)}
        data-testid="listen-skip-forward"
        aria-label="Skip forward"
        className={`${chipBase} ${chipStyle}`}
      >
        <SkipForward size={11} weight="fill" />
      </button>
      <button
        onClick={stop}
        data-testid="listen-stop"
        aria-label="Stop"
        className={`${chipBase} ${chipStyle}`}
      >
        <Stop size={12} weight="fill" />
      </button>
      <SettingsButton
        rate={rate}
        voices={langVoices}
        voiceKey={voiceKey}
        open={showSettings}
        onToggle={() => setShowSettings((v) => !v)}
        onRate={applyRate}
        onVoice={applyVoice}
        variant={variant}
      />
    </div>
  );
}

function SettingsButton({ rate, voices, voiceKey, open, onToggle, onRate, onVoice, variant }) {
  const chipBase = "inline-flex items-center gap-1.5 rounded-full font-mono-tech text-[10px] tracking-[0.28em] uppercase transition-colors";
  const chipStyle = variant === "dark"
    ? "border border-[#F5EBDC]/30 text-[#F5EBDC] hover:bg-[#F5EBDC] hover:text-[#0A1620] px-3 py-1.5"
    : "border border-[color:var(--np-brand,#B85F2A)]/60 text-[color:var(--np-brand,#B85F2A)] hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white px-3 py-1.5";
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        data-testid="listen-settings-toggle"
        aria-label="Playback settings"
        className={`${chipBase} ${chipStyle}`}
      >
        {rate}× {open ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
      </button>
      {open && (
        <div
          className="absolute z-40 mt-1 right-0 w-56 rounded-xl border border-black/10 bg-white shadow-lg p-2"
          data-testid="listen-settings-menu"
        >
          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830] px-2 pt-1">Speed</div>
          <div className="flex flex-wrap gap-1 px-2 pt-1 pb-2">
            {RATE_OPTIONS.map((r) => (
              <button
                key={r.key}
                onClick={() => onRate(r.key)}
                data-testid={`listen-rate-${r.key}`}
                className={`rounded-full px-2 py-0.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase border ${
                  Math.abs(rate - r.key) < 0.001
                    ? "bg-[#241209] text-[#F5EBDC] border-[#241209]"
                    : "bg-white border-black/15 text-[#241209] hover:border-black/40"
                }`}
              >{r.label}</button>
            ))}
          </div>
          {voices.length > 0 && (
            <>
              <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830] px-2 pt-1 border-t border-black/10">Voice</div>
              <div className="max-h-40 overflow-y-auto px-1 pt-1 pb-1">
                <button
                  onClick={() => onVoice("")}
                  data-testid="listen-voice-auto"
                  className={`w-full text-left px-2 py-1 rounded text-xs ${voiceKey === "" ? "bg-[#FBF2E1] text-[color:var(--np-brand,#B85F2A)]" : "hover:bg-[#F5EBDC]"}`}
                >
                  Auto (system default)
                </button>
                {voices.slice(0, 12).map((v) => {
                  const key = `${v.name}::${v.lang}`;
                  return (
                    <button
                      key={key}
                      onClick={() => onVoice(key)}
                      data-testid={`listen-voice-${v.name.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`w-full text-left px-2 py-1 rounded text-xs ${voiceKey === key ? "bg-[#FBF2E1] text-[color:var(--np-brand,#B85F2A)]" : "hover:bg-[#F5EBDC]"}`}
                    >
                      {v.name} <span className="text-[#3A3830]">· {v.lang}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
